import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { getAuthGeneration } from 'utils/auth';
import { meetings, type NativeBreakoutContext, type NativeContextInput } from 'services/api/meetingsClient';
import type { CallSurfaceAttendee } from 'pages/Meetings/call/types';
import type { useNativeCallSession } from './useNativeCallSession';
import { canonicalNativeRoom, joinedNativeObservation } from './nativeBreakoutObservation';

export type NativeBreakoutObservation = {
  scope: string;
  status: 'pending' | 'verified' | 'returning' | 'unavailable';
  context: NativeBreakoutContext | null;
  /** Stable peer/credential identity for retaining presentation only. */
  presentationScope?: string;
  refresh: () => Promise<void>;
  isCurrent: () => boolean;
};
export const NATIVE_CONTEXT_TIMEOUT_MS = 8000;

export function useNativeBreakoutContext({ meetingId, attendee, session, leaving }: {
  meetingId: string; attendee: CallSurfaceAttendee; panelToken: string;
  session: ReturnType<typeof useNativeCallSession>; leaving: boolean;
}) {
  const auth = useAuth();
  const generation = getAuthGeneration();
  const revision = session.observationRevision.current;
  const subject = attendee.isGuest ? `guest:${attendee.guestSessionId}` : `user:${attendee.userId ?? auth.user?.id}`;
  // Purpose-panel renewal is not ownership: requests authorize the exact login
  // or signed guest proof below, independently of the Messages capability.
  const scope = JSON.stringify([meetingId, subject, attendee.isGuest, attendee.isAuthenticated,
    attendee.breakoutToken, attendee.guestToken, auth.user?.id, generation, revision, leaving]);
  const presentationIdentity = JSON.stringify([meetingId, subject, attendee.isGuest, attendee.isAuthenticated,
    attendee.breakoutToken, attendee.guestToken, auth.user?.id, generation, revision, leaving]);
  const authLoading = useRef(auth.isAuthLoading); authLoading.current = auth.isAuthLoading;
  const current = useRef(scope); current.current = scope;
  const mounted = useRef(true);
  const requests = useRef(0);
  const failures = useRef(0);
  const retryTimer = useRef<ReturnType<typeof setTimeout>>();
  const authority = useRef<string | null>(null);
  const authorityEpoch = useRef(0);
  const presentation = useRef<{ scope: string; identity: string; context: NativeBreakoutContext } | null>(null);
  const cancelDeadline = useRef<(() => void) | null>(null);
  const [result, setResult] = useState<{ scope: string; status: NativeBreakoutObservation['status']; context: NativeBreakoutContext | null }>();
  const ready = session.snapshotSettled && !auth.isAuthLoading && !leaving
    && (attendee.isGuest || (attendee.isAuthenticated && String(auth.user?.id) === subject.slice(5)));
  const isCurrent = useCallback(() => mounted.current && current.current === scope && getAuthGeneration() === generation
    && session.observationRevision.current === revision && !leaving, [scope, generation, revision, session.observationRevision, leaving]);
  const input = useCallback((): NativeContextInput | null => {
    const peer = joinedNativeObservation(session.stateRef.current, meetingId);
    if (!ready || !isCurrent() || !peer) return null;
    const dailyUser = attendee.isGuest ? `g${attendee.guestSessionId?.replace(/-/g, '')}` : subject.slice(5);
    if (peer.userId !== dailyUser) return null;
    if (attendee.isGuest && (!attendee.breakoutToken || !attendee.guestToken)) return null;
    return { access_id: peer.accessId, observed_room_url: peer.roomUrl,
      ...(attendee.isGuest && attendee.breakoutToken ? { breakout_token: attendee.breakoutToken } : {}),
      ...(attendee.isGuest && attendee.guestToken ? { guest_token: attendee.guestToken } : {}) };
  }, [ready, isCurrent, session.stateRef, meetingId, attendee.isGuest, attendee.guestSessionId, attendee.breakoutToken, attendee.guestToken, subject]);
  const refresh = useCallback(async () => {
    cancelDeadline.current?.();
    clearTimeout(retryTimer.current);
    const request = ++requests.current;
    const payload = input();
    if (authLoading.current && presentation.current?.scope === scope && authority.current) return;
    // Revalidation of a verified scope is invisible; its authority remains
    // valid until a changed/revoked response, not merely a routine poll.
    if (presentation.current?.scope !== scope || !authority.current) setResult({ scope, status: 'pending', context: presentation.current?.identity === presentationIdentity ? presentation.current.context : null });
    let timer: ReturnType<typeof setTimeout> | undefined;
    const owns = () => isCurrent() && request === requests.current;
    try {
      const context = await Promise.race([
        payload ? meetings.fetchNativeBreakoutContext(meetingId, payload) : new Promise<never>(() => {}),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error('Context timeout')), NATIVE_CONTEXT_TIMEOUT_MS);
          cancelDeadline.current = () => { if (timer) clearTimeout(timer); reject(new Error('Retired context')); };
        }),
      ]);
      if (!owns()) return;
      const peer = joinedNativeObservation(session.stateRef.current, meetingId);
      if (!peer || context.version !== 1 || !context.revision || context.meeting_id?.toLowerCase() !== meetingId.toLowerCase()
        || context.access_id !== peer.accessId || context.observed_room_url !== peer.roomUrl
        || context.subject !== subject || context.daily_user_id !== peer.userId || context.is_owner !== peer.owner
        || !canonicalNativeRoom(context.main_room_url)
        || !['main', 'breakout', 'return_required', 'terminal'].includes(context.status)
        || (context.status === 'main' && (context.room !== null || peer.roomUrl !== context.main_room_url))
        || (['breakout', 'return_required'].includes(context.status) && (!context.room?.id || !context.room.name || !context.room.session_id))
        || (context.status === 'breakout' && (context.room?.session_id !== context.session?.id || context.session?.status !== 'active'))
      ) throw Object.assign(new Error('Context mismatch'), { code: 'NATIVE_CONTEXT_MISMATCH' });
      failures.current = 0;
      presentation.current = { scope, identity: presentationIdentity, context };
      const nextAuthority = ['main', 'breakout'].includes(context.status) ? contextAuthority(scope, context) : null;
      if (authority.current !== nextAuthority) authorityEpoch.current += 1;
      authority.current = nextAuthority;
      setResult({ scope, context, status: context.status === 'terminal' ? 'unavailable' : context.status === 'return_required' ? 'returning' : 'verified' });
    } catch (failure: any) {
      if (owns()) {
        const revoked = failure?.code === 'NATIVE_CONTEXT_MISMATCH' || [401, 403, 404, 410].includes(failure?.response?.status);
        if (!revoked && presentation.current?.scope === scope && ++failures.current < 3) {
          retryTimer.current = setTimeout(() => void refresh(), 2000 * failures.current);
          return;
        }
        authority.current = null;
        authorityEpoch.current += 1;
        setResult({ scope, status: 'unavailable', context: presentation.current?.identity === presentationIdentity ? presentation.current.context : null });
        if (!revoked) retryTimer.current = setTimeout(() => void refresh(), 30000);
      }
    } finally { if (timer) clearTimeout(timer); }
  }, [input, scope, isCurrent, meetingId, session.stateRef, subject, presentationIdentity]);
  useEffect(() => {
    mounted.current = true;
    void refresh();
    return () => { mounted.current = false; clearTimeout(retryTimer.current); requests.current += 1; cancelDeadline.current?.(); };
  }, [refresh]);
  const accepted = result?.scope === scope && isCurrent() ? result : null;
  const context = accepted?.context || (presentation.current?.identity === presentationIdentity ? presentation.current.context : null);
  const authorityKey = context ? contextAuthority(scope, context) : scope;
  const acceptedEpoch = authorityEpoch.current;
  const actionScope = `${authorityKey}:${acceptedEpoch}`;
  const observation: NativeBreakoutObservation = {
    scope: actionScope, presentationScope: `${presentationIdentity}:${context?.session?.id || ''}`, status: accepted?.status || 'pending', context, refresh,
    // A pending read does not revoke a captured action. A changed/failed read does.
    isCurrent: () => !authLoading.current && isCurrent() && authority.current === authorityKey && authorityEpoch.current === acceptedEpoch,
  };
  return { observation, input, retry: refresh, ready: observation.status === 'verified' };
}

/** Roster/transcription/name churn is presentation, not movement authority. */
function contextAuthority(scope: string, c: NativeBreakoutContext): string {
  return JSON.stringify([scope, c.status, c.is_owner, c.main_room_url, c.room?.id, c.room?.session_id,
    c.session?.id, c.session?.status, c.session?.scheduled_end_at, c.assignment?.room_id]);
}
